
import Vue from 'vue';
import { Button, Icon, RadioButton, Loading } from '@descomplica/vue-components';
import { CourseModel } from '@descomplica/dex-models';
import { ChimeraCourseModel } from '@/models/chimera/ChimeraCourseModel';
import {
  ContractExtensionEligibilityInfo,
  ContractExtensionOption,
} from '@/models/contractExtension/ContractExtensionEligibilityModel';
import { ENDPOINTS } from '@/settings/endpoints';
import { descomplicaLandingUrl } from '@/settings/constants';
import ContractExtensionRequestService from '@/helpers/postgradContractExtension/request';
import { getChimeraToken, getDexToken } from '@/helpers/get-tokens';
import { PostgradAnalytics } from '@/helpers/analytics/postgradAnalytics';
import { EVENT_LABELS } from '@/helpers/analytics/labels';
import { getDiffTime } from '@/helpers/date';
import {
  TrackPostgradContractExtensionPageExtensionButtonClick,
  TrackPostgradContractExtensionPageLandingsButtonClick,
  TrackPostgradContractExtensionPageCoursesButtonClick,
  TrackPostgradContractExtensionRequestRegistered,
  TrackPostgradContractExtensionPageSignOutButtonClick,
  TrackExpiredPostgradContractExtensionPageView,
  TrackFinishingCourseExpiredContractPageView,
  TrackBaseExpiredPostgradContractPageView,
} from '@/helpers/analytics/types';
import { getCourseCategory } from '@/helpers/category';
import { ExpiredPostgradContractMainInfo } from '@/components/postgradStudentDocuments';
import StudentDocumentRequestService from '@/helpers/postgradStudentDocuments/request';
import { CertificateEligibilityInfo } from '@/helpers/postgradStudentDocuments/interfaces';
import CertificateModal from '@/components/modals/certificate/CertificateModal.vue';
import { MessageSection } from '@/components/postgradStudentDocuments/ExpiredPostgradContractMainInfo/ExpiredPostgradContractMainInfo.vue';
import { CourseCertificateModel } from '@/models/studentDocuments';
import { ASSESSMENT_STATUS } from '@/models/greathall/assessment.model';

interface Data {
  selectedCourseName: string;
  isFetchingExtensionEligibility: boolean;
  isRegisteringExtensionRequest: boolean;
  certificateEligibilityInfo: CertificateEligibilityInfo;
  showCertificateModal: boolean;
}

export default Vue.extend({
  components: {
    Button,
    Icon,
    RadioButton,
    Loading,
    ExpiredPostgradContractMainInfo,
    CertificateModal,
  },
  data(): Data {
    return {
      selectedCourseName: '',
      isFetchingExtensionEligibility: false,
      isRegisteringExtensionRequest: false,
      certificateEligibilityInfo: {
        isEligible: false,
        approved: false,
        assessmentStatus: ASSESSMENT_STATUS.AVAILABLE,
      },
      showCertificateModal: false,
    };
  },
  computed: {
    currentCourseCertificate(): CourseCertificateModel | null {
      return this.$store.getters.certificateByCourseId(this.currentCourse.id);
    },
    loadError(): boolean {
      return (
        !this.currentCourseCertificate?.originalVersionImageUrls.every(Boolean) ||
        !this.currentCourseCertificate.socialVersionImageUrl ||
        !this.currentCourseCertificate.originalVersionPdfUrl
      );
    },
    shouldShowCertificateDownloadButton(): boolean {
      return (
        this.isEligibleToDownloadCertificate ||
        this.isGeneratingCertificate ||
        this.isAssessmentBeingCorrected
      );
    },
    dataTestId(): string {
      if (this.isEligibleToDownloadCertificate) {
        return 'expired-access-can-download';
      }

      if (this.isGeneratingCertificate) {
        return 'expired-access-generating';
      }

      if (this.isAssessmentBeingCorrected) {
        return 'expired-access-assessing';
      }

      return 'expired-blocked';
    },
    title(): string {
      if (this.isEligibleToDownloadCertificate) {
        return this.$t(
          'postgradContractExtension.expiredContractPage.courseFinishedTitle',
        ).toString();
      }

      if (this.isGeneratingCertificate) {
        return this.$t(
          'postgradContractExtension.expiredContractPage.generatingCertificateTitle',
        ).toString();
      }

      if (this.isAssessmentBeingCorrected) {
        return this.$t(
          'postgradContractExtension.expiredContractPage.correctingAssessmentTitle',
        ).toString();
      }

      if (this.canExtendCurrentContract) {
        return this.$t(
          'postgradContractExtension.expiredContractPage.accessInterrupted',
        ).toString();
      }

      return this.$t('postgradContractExtension.expiredContractPage.accessBlocked').toString();
    },
    messageSections(): MessageSection[] {
      if (this.isEligibleToDownloadCertificate) {
        return [
          {
            message: this.$t(
              'postgradContractExtension.expiredContractPage.courseFinishedPrimaryMessage',
            ).toString(),
          },
          {
            message: this.$t(
              'postgradContractExtension.expiredContractPage.courseFinishedSecondaryMessage',
            ).toString(),
          },
        ];
      }

      if (this.isGeneratingCertificate) {
        return [
          {
            message: this.$t(
              'postgradContractExtension.expiredContractPage.generatingCertificatePrimaryMessage',
            ).toString(),
          },
          {
            message: this.$t(
              'postgradContractExtension.expiredContractPage.generatingCertificateSecondaryMessage',
            ).toString(),
          },
        ];
      }

      if (this.isAssessmentBeingCorrected) {
        return [
          {
            message: this.$t(
              'postgradContractExtension.expiredContractPage.correctingAssessmentPrimaryMessage',
            ).toString(),
          },
          {
            message: this.$t(
              'postgradContractExtension.expiredContractPage.correctingAssessmentSecondaryMessage',
            ).toString(),
          },
        ];
      }

      if (this.canExtendCurrentContract) {
        return [
          {
            message: this.$t(
              'postgradContractExtension.expiredContractPage.extendableContractsTextPrimary',
            ).toString(),
          },
          {
            header: this.$t(
              'postgradContractExtension.expiredContractPage.interruptedAccessSecondaryHeader',
            ).toString(),
            message: this.$t(
              'postgradContractExtension.expiredContractPage.interruptedAccessTextSecondary',
            ).toString(),
          },
        ];
      }

      return [
        {
          message: this.$t(
            'postgradContractExtension.expiredContractPage.nonExtendableContractsTextPrimary',
          ).toString(),
        },
        {
          header: this.$t(
            'postgradContractExtension.expiredContractPage.interruptedAccessSecondaryHeader',
          ).toString(),
          message: this.$t(
            'postgradContractExtension.expiredContractPage.interruptedAccessTextSecondary',
          ).toString(),
        },
      ];
    },
    imageLink(): string {
      if (
        this.isEligibleToDownloadCertificate ||
        this.isGeneratingCertificate ||
        this.isAssessmentBeingCorrected
      ) {
        return 'https://classrooms-assets.s3.sa-east-1.amazonaws.com/post-grad/certificate.svg';
      }

      return 'https://classrooms-assets.s3.sa-east-1.amazonaws.com/post-grad/curso-expirado-modal.svg';
    },
    isEligibleToDownloadCertificate(): boolean {
      return this.certificateEligibilityInfo.isEligible;
    },
    isGeneratingCertificate(): boolean {
      return (
        !this.isEligibleToDownloadCertificate &&
        this.certificateEligibilityInfo.approved &&
        this.certificateEligibilityInfo.assessmentStatus === ASSESSMENT_STATUS.PASSED
      );
    },
    isAssessmentBeingCorrected(): boolean {
      return (
        !this.isEligibleToDownloadCertificate &&
        this.certificateEligibilityInfo.assessmentStatus === ASSESSMENT_STATUS.WAITING_GRADING
      );
    },
    currentCourse(): CourseModel {
      return this.$store.getters.currentCourse;
    },
    expiredChimeraCourseById(): ChimeraCourseModel {
      return this.$store.getters.expiredChimeraCourseById(this.currentCourse.descomplicaCourseId);
    },
    chimeraCourses(): ChimeraCourseModel[] {
      return this.$store.getters.getChimeraUserCourses;
    },
    courseEligibleForExtensionInformation(): ContractExtensionEligibilityInfo | null {
      return this.$store.getters.contractExtensionEligibilityInformationByCourseId(
        this.currentCourse.id,
      );
    },
    extensionOptionsPeriods(): number[] {
      return (
        this.courseEligibleForExtensionInformation?.extensionOptions.map((o) => o.period) || []
      );
    },
    canExtendCurrentContract(): boolean {
      return !!this.courseEligibleForExtensionInformation?.extensionOptions.length;
    },
    otherUserCourses(): CourseModel[] {
      const courses: CourseModel[] = this.$store.getters.currentUser?.getAllCourses() || [];

      return courses.filter((c) => c.id !== this.currentCourse.id);
    },
    selectedCourse(): CourseModel | null {
      return this.otherUserCourses.find((c) => c.name === this.selectedCourseName) || null;
    },
    userHasOtherCourses(): boolean {
      return this.otherUserCourses.length > 0;
    },
    daysAfterContractExpiration(): number | undefined {
      const { mostRecentExpiredCourseContract } = this.expiredChimeraCourseById;

      if (!mostRecentExpiredCourseContract) return undefined;

      const todayDate = new Date();
      return getDiffTime(todayDate, mostRecentExpiredCourseContract.expirationDate).days;
    },
    canViewPostgradCertificate(): boolean {
      return this.$store.getters.canViewPostgradCertificate;
    },
    defaultTrackingProps(): TrackBaseExpiredPostgradContractPageView {
      return {
        course: this.currentCourse.name,
        category: 'pos',
      };
    },
    extensionTrackingProps(): TrackExpiredPostgradContractExtensionPageView {
      return {
        ...this.defaultTrackingProps,
        in_postgrad_extension_page: true,
        days_after_contract_expiration: this.daysAfterContractExpiration,
        available_extension_options: this.extensionOptionsPeriods,
        available_extension_options_quantity: this.extensionOptionsPeriods.length,
        ...(!!this.courseEligibleForExtensionInformation?.extensionOptions.length && {
          can_extend_contract: this.canExtendCurrentContract,
        }),
      };
    },
    finishingCourseTrackingProps(): TrackFinishingCourseExpiredContractPageView {
      return {
        ...this.defaultTrackingProps,
        in_finishing_course_page: true,
        eligible_to_download_certificate: this.certificateEligibilityInfo.isEligible,
        assessments_status: this.certificateEligibilityInfo.assessmentStatus,
      };
    },
    userIsFinishingCourse(): boolean {
      return [ASSESSMENT_STATUS.PASSED, ASSESSMENT_STATUS.WAITING_GRADING].some(
        (s) => s === this.certificateEligibilityInfo.assessmentStatus,
      );
    },
    pageTrackingProps():
      | TrackFinishingCourseExpiredContractPageView
      | TrackExpiredPostgradContractExtensionPageView {
      return this.userIsFinishingCourse
        ? this.finishingCourseTrackingProps
        : this.extensionTrackingProps;
    },
  },
  async mounted() {
    if (!this.expiredChimeraCourseById) {
      this.$router.push({ name: 'home' });
      return;
    }

    this.isFetchingExtensionEligibility = true;

    await this.$store.dispatch('GET_POSTGRAD_CONTRACT_EXTENSION_ELIGIBILITY', {
      course: this.currentCourse,
      chimeraCourse: this.expiredChimeraCourseById,
    });

    if (this.canViewPostgradCertificate) {
      const result = await StudentDocumentRequestService.getCertificateEligibility(
        getDexToken()!,
        getChimeraToken(),
        this.currentCourse,
      );

      this.certificateEligibilityInfo = result || this.certificateEligibilityInfo;
    }

    this.isFetchingExtensionEligibility = false;

    PostgradAnalytics.trackEvent<
      TrackExpiredPostgradContractExtensionPageView | TrackFinishingCourseExpiredContractPageView
    >(EVENT_LABELS.postgradExpiredContractPageView, this.pageTrackingProps);
  },
  methods: {
    toggleCertificateModal() {
      this.showCertificateModal = !this.showCertificateModal;
    },
    async redirectToCheckout(option: ContractExtensionOption) {
      PostgradAnalytics.trackEvent<TrackPostgradContractExtensionPageExtensionButtonClick>(
        EVENT_LABELS.buttonClicked,
        {
          ...this.pageTrackingProps,
          postgrad_expired_contract_page: true,
          checkout_button: true,
          chosen_extension_option: option.period,
        },
      );

      if (
        !this.expiredChimeraCourseById.mostRecentExpiredCourseContract ||
        !this.courseEligibleForExtensionInformation
      )
        return;

      this.isRegisteringExtensionRequest = true;

      const hasRequestBeenRegistered =
        await ContractExtensionRequestService.registerContractExtensionRequest({
          course: this.currentCourse,
          chimeraContractId: +this.expiredChimeraCourseById.mostRecentExpiredCourseContract.id,
          chimeraToken: getChimeraToken(),
          period: option.period,
        });

      if (!hasRequestBeenRegistered) {
        this.isRegisteringExtensionRequest = false;
        return;
      }

      PostgradAnalytics.trackEvent<TrackPostgradContractExtensionRequestRegistered>(
        EVENT_LABELS.postgradRequestContractExtensionSuccessful,
        {
          ...this.pageTrackingProps,
          postgrad_expired_contract_page: true,
          chosen_extension_option: option.period,
        },
      );

      window.location.replace(option.url);
    },
    redirectToLanding(): void {
      if (!descomplicaLandingUrl) return;

      PostgradAnalytics.trackEvent<TrackPostgradContractExtensionPageLandingsButtonClick>(
        EVENT_LABELS.buttonClicked,
        {
          postgrad_expired_contract_page: true,
          landing_button: true,
          days_after_contract_expiration: this.daysAfterContractExpiration,
          ...this.pageTrackingProps,
        },
      );

      window.location.replace(`${descomplicaLandingUrl}/pos-graduacao`);
    },
    redirectToCourse() {
      if (!this.selectedCourse) return;

      PostgradAnalytics.trackEvent<TrackPostgradContractExtensionPageCoursesButtonClick>(
        EVENT_LABELS.buttonClicked,
        {
          postgrad_expired_contract_page: true,
          course_button: true,
          ...this.pageTrackingProps,
        },
      );

      window.location.replace(
        `${ENDPOINTS.classrooms}/${this.selectedCourse.descomplicaCategorySlug}/${this.selectedCourse.slug}`,
      );
    },
    signOut() {
      PostgradAnalytics.trackEvent<TrackPostgradContractExtensionPageSignOutButtonClick>(
        EVENT_LABELS.buttonClicked,
        {
          postgrad_expired_contract_page: true,
          sign_out_button: true,
          days_after_contract_expiration: this.daysAfterContractExpiration,
          ...this.pageTrackingProps,
        },
      );

      this.$store.commit('SIGNOUT');
      window.location.reload();
    },
    isCourseExpired(course: CourseModel): boolean {
      if (getCourseCategory(course) !== 'pos') return false;

      const chimeraCourse: ChimeraCourseModel | undefined = this.chimeraCourses.find(
        (c) => c._id === course.descomplicaCourseId,
      );

      if (!chimeraCourse) return true;

      return chimeraCourse.isExpired();
    },
  },
});
